/**
 * BLRRD paint engine — stroke state machine + dab-path walker (Phase
 * 4.C.paint.1, Wave 9.1).
 *
 * DOM-pure. Owns the spacing accumulator that converts a sequence of
 * pointer positions into evenly-spaced dab centres, and the dirty-bbox
 * accumulator that feeds LODScheduler.requestFull({region}) (dossier §1.1,
 * §1.3, §4.2). The actual per-dab stamping is injected as a callback so
 * paint-core stays decoupled from the dab ops (brush/pencil/eraser, and
 * the Wave-9.2/9.3 clone/smudge/dodge-burn/heal tools).
 *
 * Spacing accumulator (dossier §1.3):
 *   dab_spacing = max(1, brush_size · spacing_ratio).  Between two
 *   consecutive pointer positions p_prev → p_curr we walk the segment in
 *   steps of dab_spacing, carrying leftover distance from the previous
 *   segment so spacing stays pixel-accurate across short events and
 *   direction reversals (without the leftover, slow zigzags clump dabs).
 *   v1 uses linear interpolation between events — cubic Bezier is a v1.x
 *   polish item (dossier §1.3).
 *
 * Provenance (clean-room): Hughes/van Dam §10.5 (stamped-brush path walk).
 * NO GIMP/GEGL source consulted.
 *
 * @module src/pipeline/paint/paint-core
 */

/* Minimum dab spacing in pixels — below 1 px we'd stamp the same pixel
 * repeatedly with no visual gain and quadratic cost. */
const MIN_SPACING_PX = 1;

/* Default spacing ratio (fraction of brush diameter between dabs). 0.1 →
 * ~10 dabs per brush-diameter of travel — the classic soft-line default. */
const DEFAULT_SPACING_RATIO = 0.1;

/**
 * Create a fresh stroke state. Call once on pointer-down.
 *
 * @param {object} opts
 * @param {number} opts.x          start x (image pixels)
 * @param {number} opts.y          start y
 * @param {number} [opts.pressure=0.5]
 * @param {number} [opts.time=0]   pointer-down timestamp (ms)
 * @returns {object} mutable stroke state
 */
export function startStroke(opts) {
    const o = opts || {};
    return {
        prevX: Number(o.x) || 0,
        prevY: Number(o.y) || 0,
        prevPressure: Number.isFinite(o.pressure) ? o.pressure : 0.5,
        prevTime: Number.isFinite(o.time) ? o.time : 0,
        leftover: 0,             /* carried distance from prior segment */
        started: false,          /* has the very first dab landed? */
        dirty: null,             /* accumulated dirty bbox {x0,y0,x1,y1} */
        dabCount: 0,
    };
}

/**
 * Merge a dab's dirty region into the stroke's accumulated bbox.
 * Region shape is {x, y, w, h}; null is ignored.
 */
function accumulateDirty(state, region) {
    if (!region) return;
    const x0 = region.x;
    const y0 = region.y;
    const x1 = region.x + region.w - 1;
    const y1 = region.y + region.h - 1;
    if (!state.dirty) {
        state.dirty = { x0, y0, x1, y1 };
        return;
    }
    const d = state.dirty;
    if (x0 < d.x0) d.x0 = x0;
    if (y0 < d.y0) d.y0 = y0;
    if (x1 > d.x1) d.x1 = x1;
    if (y1 > d.y1) d.y1 = y1;
}

/**
 * Resolve the dab spacing in pixels from a brush size + spacing ratio.
 *
 * @param {number} brushSize     effective brush diameter
 * @param {number} spacingRatio  fraction of diameter (e.g. 0.1)
 * @returns {number} spacing in pixels (≥ MIN_SPACING_PX)
 */
export function dabSpacing(brushSize, spacingRatio) {
    const ratio = Number.isFinite(spacingRatio) && spacingRatio > 0
        ? spacingRatio : DEFAULT_SPACING_RATIO;
    const s = brushSize * ratio;
    return s < MIN_SPACING_PX ? MIN_SPACING_PX : s;
}

/**
 * Advance the stroke to a new pointer position, emitting one dab per
 * `spacing` pixels of travel via the injected `stamp` callback. The very
 * first call after startStroke also lays a dab at the start position so a
 * single tap (down→up with no move) still paints.
 *
 * The `stamp` callback receives `(dabX, dabY, dabPressure)` and must
 * return the dab's dirty region ({x,y,w,h}|null); paint-core unions it
 * into the stroke bbox. paint-core does NOT compute the brush mask or
 * touch any buffer — that's the caller's job inside `stamp`.
 *
 * @param {object} state         from startStroke
 * @param {object} ev
 * @param {number} ev.x          new x
 * @param {number} ev.y          new y
 * @param {number} [ev.pressure=0.5]
 * @param {number} [ev.time=0]
 * @param {number} ev.spacing    dab spacing in pixels (caller resolves via
 *                               dabSpacing(); may vary per move if pressure
 *                               scales the brush)
 * @param {(x:number,y:number,pressure:number)=>({x:number,y:number,w:number,h:number}|null)} stamp
 * @returns {number} number of dabs emitted this call
 */
export function strokeTo(state, ev, stamp) {
    const cx = Number(ev.x) || 0;
    const cy = Number(ev.y) || 0;
    const cp = Number.isFinite(ev.pressure) ? ev.pressure : 0.5;
    const spacing = Number.isFinite(ev.spacing) && ev.spacing > 0
        ? ev.spacing : MIN_SPACING_PX;

    let emitted = 0;

    /* First dab of the stroke lands exactly at the start position. */
    if (!state.started) {
        const region = stamp(state.prevX, state.prevY, state.prevPressure);
        accumulateDirty(state, region);
        state.started = true;
        state.dabCount++;
        emitted++;
    }

    const dx = cx - state.prevX;
    const dy = cy - state.prevY;
    const segLen = Math.sqrt(dx * dx + dy * dy);

    if (segLen <= 1e-9) {
        /* No travel — nothing to interpolate; keep prev/leftover as-is. */
        state.prevPressure = cp;
        state.prevTime = Number.isFinite(ev.time) ? ev.time : state.prevTime;
        return emitted;
    }

    const invSeg = 1 / segLen;
    /* `leftover` is distance already accumulated toward the next dab from
     * prior segments. The first dab on THIS segment lands at distance
     * (spacing − leftover) from prevX; subsequent dabs every `spacing`.
     * Carrying leftover across segments keeps spacing pixel-accurate at
     * direction reversals (no zigzag clump — dossier §1.3). */
    let d = spacing - state.leftover;
    if (d < 0) d = 0;                         /* leftover ≥ spacing edge case */
    while (d <= segLen + 1e-9) {
        const t = d * invSeg;                 /* 0..1 along the segment */
        const px = state.prevX + dx * t;
        const py = state.prevY + dy * t;
        const pp = state.prevPressure + (cp - state.prevPressure) * t;
        const region = stamp(px, py, pp);
        accumulateDirty(state, region);
        state.dabCount++;
        emitted++;
        d += spacing;
    }
    /* Distance from the last emitted dab (at d − spacing) to the segment
     * end becomes the new leftover. If no dab landed this segment, the
     * whole segment adds to the accumulator. */
    state.leftover = segLen - (d - spacing);
    if (state.leftover < 0) state.leftover = 0;

    state.prevX = cx;
    state.prevY = cy;
    state.prevPressure = cp;
    state.prevTime = Number.isFinite(ev.time) ? ev.time : state.prevTime;
    return emitted;
}

/**
 * Finish the stroke. Returns the accumulated dirty region as a {x,y,w,h}
 * object (or null if no dab landed), optionally padded by `pad` pixels on
 * every side (used to account for kernel halos like a downstream blur —
 * dossier §4.2) and clamped to the buffer extent.
 *
 * @param {object} state
 * @param {number} [pad=0]   padding in pixels
 * @param {number} [w]       buffer width for clamping (optional)
 * @param {number} [h]       buffer height for clamping (optional)
 * @returns {{x:number,y:number,w:number,h:number}|null}
 */
export function finishStroke(state, pad = 0, w, h) {
    if (!state || !state.dirty) return null;
    const d = state.dirty;
    let x0 = d.x0 - pad;
    let y0 = d.y0 - pad;
    let x1 = d.x1 + pad;
    let y1 = d.y1 + pad;
    if (Number.isFinite(w)) {
        if (x0 < 0) x0 = 0;
        if (x1 > w - 1) x1 = w - 1;
    } else if (x0 < 0) {
        x0 = 0;
    }
    if (Number.isFinite(h)) {
        if (y0 < 0) y0 = 0;
        if (y1 > h - 1) y1 = h - 1;
    } else if (y0 < 0) {
        y0 = 0;
    }
    if (x1 < x0 || y1 < y0) return null;
    return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

export { MIN_SPACING_PX, DEFAULT_SPACING_RATIO };
